import { useState } from 'react';
import { Confirm, DataTable, toast, type Column, type Destructive } from '@drbell/ui';
import {
  api, ApiError, MANAGERS, type Member, type OrgDetail, type OrgRole,
  type OrgSummary, type Resident,
} from '../api.ts';

const ASSIGNABLE: OrgRole[] = ['admin', 'staff', 'clinician', 'billing'];

const ROLE_MEANS: Record<OrgRole, string> = {
  owner: 'Everything, including erasing the organisation.',
  admin: 'Manage staff and residents. Cannot erase the organisation.',
  staff: 'See assigned residents and record doses. No history.',
  clinician: 'Read medication history and adherence. Cannot record doses.',
  billing: 'Seats and invoices only. No access to any resident.',
};

/**
 * Staff, their roles and their ward scope.
 *
 * Scope is the thing worth getting right in the UI: `patientScope = null`
 * means the whole building and a list means one ward, and those two are a
 * single click apart. Showing which one is in force, in words, on every row
 * is the difference between a nurse seeing her four residents and a nurse
 * seeing ninety people's medication records.
 *
 * On the shared table for the same reason the residents list is: a home with
 * forty staff needs to find one of them, and a hand-rolled <ul> gives no
 * search, no sort and no column choice. Removal now goes through Confirm
 * rather than the browser's confirm() -- revoking someone's access at 3am
 * deserves a sentence saying what happens, not a yes/no box quoting a phone
 * number.
 */
export function Staff({ org, detail, residents, onChanged }: {
  org: OrgSummary;
  detail: OrgDetail;
  residents: Resident[];
  onChanged: () => void;
}) {
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);
  const [search, setSearch] = useState('');

  const canManage = MANAGERS.includes(detail.yourRole);

  if (!canManage) {
    return (
      <p className="rounded-xl border border-dashed border-(--line) px-4 py-8 text-center text-(--muted)">
        Only an owner or admin can see and change the staff list.
      </p>
    );
  }

  const q = search.trim().toLowerCase();
  const rows = detail.members.filter(
    (m) =>
      !q ||
      (m.name ?? '').toLowerCase().includes(q) ||
      (m.phone ?? '').includes(q) ||
      m.role.includes(q),
  );

  const columns: Column<Member>[] = [
    {
      key: 'name',
      header: 'Person',
      cell: (m) => {
        const shown = m.name ?? m.phone ?? 'Unnamed account';
        return (
          <div className="min-w-0">
            <div className="font-medium">{shown}</div>
            {/* Compared, not just null-checked. An account with no name of its
                own comes back with `name` set to the phone number, so
                `name && phone` is true and the number was printed twice. */}
            {m.phone && m.phone !== shown && (
              <div className="text-xs text-(--muted)">{m.phone}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      cell: (m) => (
        <span title={ROLE_MEANS[m.role]} className="capitalize">{m.role}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (m) =>
        m.status === 'invited' ? (
          <span className="rounded-full border border-amber-500/40 px-2 py-0.5 text-xs text-amber-600">
            Not yet accepted
          </span>
        ) : (
          <span className="text-(--muted) capitalize">{m.status}</span>
        ),
    },
    {
      key: 'scope',
      header: 'Sees',
      cell: (m) => <ScopeBadge member={m} residents={residents} />,
    },
    {
      key: 'actions',
      header: '',
      width: '11rem',
      cell: (m) =>
        m.role === 'owner' ? (
          /* Said rather than left blank: an empty cell reads as a bug, and
             "why can I not remove the owner" is a support ticket. */
          <span className="block text-right text-xs text-(--muted)">Signs for the home</span>
        ) : (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(m)}
              className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-(--accent)"
            >
              Change
            </button>
            <button
              onClick={() =>
                setPending({
                  title: `Remove ${m.name ?? m.phone ?? 'this person'} from ${org.name}?`,
                  body:
                    m.status === 'invited'
                      ? 'The invitation is withdrawn. They have never seen anything, so nothing is lost.'
                      : 'Their access ends immediately — on this console, on the ward tablet and on their phone. Doses they have already recorded stay in the record, with their name on them.',
                  reassurance:
                    m.status === 'invited'
                      ? undefined
                      : 'Nothing is erased. Invite them again at any time and their access returns.',
                  confirmLabel: m.status === 'invited' ? 'Withdraw invitation' : 'End their access',
                  // Confirm shows a throw inside its own dialog; letting it
                  // out is how the failure gets reported.
                  onConfirm: async () => {
                    await api.del(`/api/orgs/${org.id}/members/${m.userId}`);
                    toast.success(
                      `${m.name ?? m.phone ?? 'They'} no longer has access`,
                      'Doses they recorded keep their name on them.',
                    );
                    onChanged();
                  },
                })
              }
              className="min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-red-500"
            >
              Remove
            </button>
          </div>
        ),
    },
  ];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Staff ({detail.members.length})</h2>
        <button
          onClick={() => setInviting(true)}
          className="ml-auto min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
        >
          Invite a colleague
        </button>
      </div>

      {inviting && (
        <InviteStaff
          orgId={org.id}
          onDone={(added) => {
            setInviting(false);
            if (added) onChanged();
          }}
        />
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(m) => m.userId}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search by name, number or role…"
        noun="staff"
        storageKey="org-staff"
      />

      {editing && (
        <EditMember
          orgId={org.id}
          orgName={org.name}
          youAreOwner={detail.yourRole === 'owner'}
          member={editing}
          residents={residents}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

/**
 * Says what the scope *is*, not just that one exists.
 *
 * "Ward: 4 residents" is the number that matters, and naming them on hover
 * means a mis-set scope is visible without opening the editor.
 */
function ScopeBadge({ member, residents }: { member: Member; residents: Resident[] }) {
  if (member.patientScope === null) {
    return (
      <span className="rounded-full border border-(--line) px-2 py-0.5 text-xs text-(--muted)">
        All residents
      </span>
    );
  }
  const names = member.patientScope
    .map((id) => residents.find((r) => r.id === id)?.displayName ?? id)
    .join(', ');

  /*
   * Zero is amber, not green.
   *
   * "4 assigned" and "No residents assigned" were the same reassuring green,
   * which made a nurse who can see nobody look correctly configured. An empty
   * scope is a legitimate state -- it is how you suspend someone without
   * removing them -- but it is almost never what was meant, so it should
   * catch the eye rather than blend in.
   */
  if (member.patientScope.length === 0) {
    return (
      <span
        title="This person can open nobody's record. Usually a scope saved before anyone was ticked."
        className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs text-amber-700"
      >
        No residents assigned
      </span>
    );
  }

  return (
    <span
      title={names}
      className="rounded-full bg-(--accent-soft) px-2.5 py-0.5 text-xs text-(--accent)"
    >
      {member.patientScope.length} assigned
    </span>
  );
}

function EditMember({ orgId, orgName, youAreOwner, member, residents, onClose, onSaved }: {
  orgId: string;
  orgName: string;
  /** Only an owner can make someone an owner (defect 141). */
  youAreOwner: boolean;
  member: Member;
  residents: Resident[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState<OrgRole>(member.role);
  const [scoped, setScoped] = useState(member.patientScope !== null);
  const [picked, setPicked] = useState<string[]>(member.patientScope ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingOwner, setConfirmingOwner] = useState(false);
  const roles: OrgRole[] = youAreOwner && member.status === 'active' ? ['owner', ...ASSIGNABLE] : ASSIGNABLE;

  const save = async () => {
    // Handing over the home is confirmed separately: it cannot be taken back
    // by the person handing it over alone, once there are two owners.
    if (role === 'owner' && member.role !== 'owner' && !confirmingOwner) {
      setConfirmingOwner(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/orgs/${orgId}/members/${member.userId}`, {
        role,
        patientScope: scoped ? picked : null,
      });
      toast.success(
        `${member.name ?? 'They'} is now ${role}`,
        scoped ? `Confined to ${picked.length} resident${picked.length === 1 ? '' : 's'}.` : 'Sees every resident.',
      );
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="staff-dialog-title">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-(--line) bg-(--bg) p-5">
      <h2 id="staff-dialog-title" className="mb-4 text-lg font-semibold">
        {member.name ?? member.phone ?? 'This person'}
      </h2>
      <label className="mb-1 block text-xs font-medium text-(--muted)">Role</label>
      <select
        value={role}
        onChange={(e) => setRole(e.target.value as OrgRole)}
        className="mb-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--bg) px-3"
      >
        {roles.map((r) => (
          <option key={r} value={r}>{r[0]!.toUpperCase() + r.slice(1)}</option>
        ))}
      </select>
      <p className="mb-3 text-xs text-(--muted)">{ROLE_MEANS[role]}</p>
      {confirmingOwner && (
        <p role="alert" className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          {member.name ?? 'They'} will be an owner of {orgName}: everything you can do, including erasing
          the organisation and changing your own role. Press Save again to confirm.
        </p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={scoped}
          onChange={(e) => setScoped(e.target.checked)}
        />
        Confine to specific residents
      </label>
      <p className="mb-2 mt-1 text-xs text-(--muted)">
        {scoped
          ? 'They will see only the residents ticked below.'
          : 'They will see every resident this organisation holds.'}
      </p>

      {scoped && (
        <div className="max-h-48 overflow-y-auto rounded-lg border border-(--line) bg-(--bg) p-2">
          {residents.length === 0 && (
            <p className="px-1 py-2 text-xs text-(--muted)">No residents to assign yet.</p>
          )}
          {residents.map((r) => (
            <label key={r.id} className="flex items-center gap-2 px-1 py-1 text-sm">
              <input
                type="checkbox"
                checked={picked.includes(r.id)}
                onChange={(e) =>
                  setPicked(
                    e.target.checked
                      ? [...picked, r.id]
                      : picked.filter((x) => x !== r.id),
                  )
                }
              />
              {r.displayName}
              {r.locationNote && <span className="text-xs text-(--muted)">({r.locationNote})</span>}
            </label>
          ))}
        </div>
      )}

      {scoped && picked.length === 0 && (
        <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs">
          With nothing ticked they will see no residents at all. That is a valid
          way to suspend someone without removing them, but it is rarely what is
          meant.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
          Cancel
        </button>
        <button
          disabled={busy}
          onClick={() => void save()}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      </div>
    </div>
  );
}

function InviteStaff({ orgId, onDone }: { orgId: string; onDone: (added: boolean) => void }) {
  const [phone, setPhone] = useState('+91');
  const [role, setRole] = useState<OrgRole>('staff');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/orgs/${orgId}/members`, { phone, role, patientScope: null });
      toast.success('Invitation sent', 'They see nothing until they accept it.');
      onDone(true);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 404
          ? 'No DrBell account uses that number. Ask them to install the app and sign in first — staff on a system holding medical records are not created from a typed phone number.'
          : e instanceof ApiError ? e.message : String(e),
      );
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-(--line) bg-(--panel) p-4">
      <h3 className="mb-3 font-medium">Invite a colleague</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-(--muted)">Their phone number</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoFocus
            className="min-h-11 w-full rounded-lg border border-(--line) bg-(--bg) px-3 outline-none focus:border-(--accent)"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-(--muted)">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as OrgRole)}
            className="min-h-11 w-full rounded-lg border border-(--line) bg-(--bg) px-3"
          >
            {ASSIGNABLE.map((r) => (
          <option key={r} value={r}>{r[0]!.toUpperCase() + r.slice(1)}</option>
        ))}
          </select>
        </label>
      </div>
      <p className="mt-2 text-xs text-(--muted)">{ROLE_MEANS[role]}</p>
      <p className="mt-1 text-xs text-(--muted)">
        They start with access to every resident, and must accept before they see
        anything. Narrow them to a ward once they have.
      </p>

      {error && (
        <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          disabled={busy || !/^\+[1-9]\d{7,14}$/.test(phone)}
          onClick={() => void submit()}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Inviting…' : 'Send invitation'}
        </button>
        <button onClick={() => onDone(false)} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
